import {useCallback} from 'react';
import {CustomContains, FilterOption, SearchFilterFn, SelectedFilters, SetResultsListFn} from './FiltersBar';

//helper function tests if target string contains any item in array
function containsAnyOf(target, array): boolean {
    let value = 0;
    array.forEach((word) => {
        value = value + target.includes(word);
    });
    return value >= 1;
}

interface filterBarFnsProps {
    filterResultList: ({
        search,
        selectedFilters
    }: {
        search?: string;
        selectedFilters?: {os; twinmotionVersions};
    }) => void;
    updateSelectedFilters: (e, filterLabel, value, isChecked) => void;
}

export const useFilterFns = <T>(
    selectedFilters: SelectedFilters,
    setSelectedFilters: (value: SelectedFilters) => void,
    setResultsList: SetResultsListFn<T> | undefined,
    fullResultsList: T[],
    search?: string,
    searchFilterFn?: SearchFilterFn<T>,
    filterOptions?: FilterOption<T>[]
): filterBarFnsProps => {
    const filterResultList = useCallback(
        ({
            search,
            selectedFilters
        }: {
            search?: string;
            selectedFilters?: Record<string, string[]>;
        }): void => {
            if (typeof setResultsList !== 'function') {
                //filtering is manually handled by the parent component
                return;
            }
            let filteredResults = fullResultsList;
            if (search && typeof searchFilterFn === 'function') {
                filteredResults = searchFilterFn(filteredResults, search);
            }

            if (selectedFilters) {
                for (const [filterName, filterValueList] of Object.entries(selectedFilters)) {
                    let customContains: CustomContains<T>;
                    if (filterOptions && filterOptions.length) {
                        const filterOption = filterOptions.find(
                            (option) => option.name === filterName
                        );
                        if (typeof filterOption?.customContains === 'function') {
                            customContains = filterOption.customContains;
                        }
                    }
                    filteredResults = filteredResults?.filter((result) => {
                        if (customContains && result) {
                            return customContains(filterName, result, filterValueList || []);
                        }
                        return containsAnyOf(result?.[filterName], filterValueList);
                    });
                }
            }

            setResultsList(filteredResults);
        },
        [selectedFilters, setSelectedFilters, setResultsList]
    );

    const updateSelectedFilters = useCallback(
        (e, filterLabel, value, isChecked) => {
            e.preventDefault();
            const updatedFilters = Object.assign({}, selectedFilters);

            const filterArray = selectedFilters[filterLabel] || [];
            const exists = Object.prototype.hasOwnProperty.call(selectedFilters, filterLabel);
            const index = filterArray.indexOf(value);

            if (Array.isArray(filterArray)) {
                //if checked, append filter to list
                index === -1 && filterArray.push(value);

                //if unchecked, remove filter from list
                if (exists) {
                    if (index > -1) {
                        filterArray.splice(index, 1);
                    }
                }

                //Update filters object with selected filters or delete filter key from object if none select
                filterArray.length > 0
                    ? (updatedFilters[filterLabel] = filterArray)
                    : delete updatedFilters[filterLabel];
            }

            setSelectedFilters(updatedFilters);
            filterResultList({search, selectedFilters: updatedFilters});
        },
        [selectedFilters, setSelectedFilters]
    );

    return {
        filterResultList,
        updateSelectedFilters
    };
};
